import { useState, useEffect, useRef, useCallback } from "react";
import { WebSocketService } from "../services/websocketService";
import {
  WebSocketMessage,
  WebSocketState,
  WebSocketConfig,
  MessageType,
  InitiativeEntry,
} from "../types/websocket";

interface UseWebSocketOptions {
  autoConnect?: boolean;
  onMessage?: (_message: WebSocketMessage) => void;
  onStateChange?: (_state: WebSocketState) => void;
  onError?: (_error: Event) => void;
}

interface UseWebSocketReturn {
  state: WebSocketState;
  isConnected: boolean;
  messages: WebSocketMessage[];
  lastMessage: WebSocketMessage | null;
  websocketService: WebSocketService | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (
    _type: MessageType,
    _data: unknown,
    _recipients?: string[],
    _sessionId?: string
  ) => boolean;
  sendChatMessage: (
    _message: string,
    _messageType?: string,
    _characterName?: string,
    _recipientId?: string,
    _sessionId?: string
  ) => boolean;
  sendDiceRoll: (
    _formula: string,
    _rollName?: string,
    _isPrivate?: boolean,
    _characterId?: string,
    _sessionId?: string,
    _characterName?: string
  ) => boolean;
  shareAsset: (
    _assetId: string,
    _assetName: string,
    _assetType: string,
    _permissions?: string[],
    _sessionId?: string
  ) => boolean;
  sendPing: () => boolean;
  joinSession: (_sessionId: string, _characterId?: string, _characterName?: string) => boolean;
  leaveSession: (_sessionId: string) => boolean;
  clearMessages: () => void;
  prependMessages: (_messages: WebSocketMessage[]) => void;
  updateSessionId: (_sessionId: string) => void;
  connectionInfo: { state: WebSocketState; reconnectAttempts: number; sessionId?: string };

  // Initiative and combat management
  sendInitiativeUpdate: (
    _sessionId: string,
    _entries: InitiativeEntry[],
    _currentTurn: number,
    _round: number,
    _isTracking: boolean
  ) => boolean;
  addToInitiative: (_sessionId: string, _entry: InitiativeEntry) => boolean;
  removeFromInitiative: (_sessionId: string, _entryId: string) => boolean;
  changeTurn: (_sessionId: string, _direction: "next" | "prev") => boolean;
  updateCombatState: (_sessionId: string, _state: string) => boolean;
  updateHealth: (
    _sessionId: string,
    _entryId: string,
    _hp: number,
    _maxHp: number,
    _change: number,
    _changeType: "damage" | "healing"
  ) => boolean;
  updateStatusEffects: (
    _sessionId: string,
    _entryId: string,
    _conditions: string[],
    _added?: string[],
    _removed?: string[]
  ) => boolean;
  sendSessionControl: (_sessionId: string, _action: string) => boolean;
}

export const useWebSocket = (
  config: WebSocketConfig,
  options: UseWebSocketOptions = {}
): UseWebSocketReturn => {
  const [state, setState] = useState<WebSocketState>(WebSocketState.DISCONNECTED);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [connectionInfo, setConnectionInfo] = useState<{
    state: WebSocketState;
    reconnectAttempts: number;
    sessionId?: string;
  }>({
    state: WebSocketState.DISCONNECTED,
    reconnectAttempts: 0,
  });

  const wsService = useRef<WebSocketService | null>(null);
  const { autoConnect = true, onMessage, onStateChange, onError } = options;

  // Stabilize callback handlers to prevent recreation loops
  const stableOnMessage = useCallback(
    (message: WebSocketMessage) => {
      setMessages((prev) => [...prev, message]);
      setLastMessage(message);
      onMessage?.(message);
    },
    [onMessage]
  );

  const stableOnStateChange = useCallback(
    (newState: WebSocketState) => {
      setState(newState);
      setConnectionInfo(
        wsService.current?.getConnectionInfo() || {
          state: newState,
          reconnectAttempts: 0,
        }
      );
      onStateChange?.(newState);
    },
    [onStateChange]
  );

  const stableOnError = useCallback(
    (error: Event) => {
      console.error("❌ WebSocket hook: Error", error);
      onError?.(error);
    },
    [onError]
  );

  // Initialize WebSocket service - only recreate when config actually changes
  useEffect(() => {
    // Don't recreate if we already have a service with the same config
    if (wsService.current) {
      wsService.current.disconnect();
    }

    wsService.current = new WebSocketService(config);

    wsService.current.setEventHandlers({
      onConnect: () => {},
      onDisconnect: () => {},
      onError: stableOnError,
      onMessage: stableOnMessage,
      onStateChange: stableOnStateChange,
    });

    // Auto-connect if enabled (but only once)
    if (autoConnect) {
      // Add a small delay to prevent immediate reconnection loops
      const timeoutId = setTimeout(() => {
        wsService.current?.connect().catch(console.error);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        wsService.current?.disconnect();
      };
    }

    // Cleanup on unmount
    return () => {
      wsService.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.url, config.sessionId, config.userId, config.username, config.token, autoConnect]); // Only depend on config values that matter

  // Handle page visibility changes for better reconnection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && autoConnect) {
        // When page becomes visible, check if we need to reconnect
        if (wsService.current && !wsService.current.isConnected()) {
          wsService.current.connect().catch(console.error);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [autoConnect]);

  // Connection methods
  const connect = useCallback(async (): Promise<void> => {
    if (wsService.current) {
      await wsService.current.connect();
    }
  }, []);

  const disconnect = useCallback((): void => {
    wsService.current?.disconnect();
  }, []);

  // Message sending methods
  const sendMessage = useCallback(
    (_type: MessageType, _data: unknown, _recipients?: string[], _sessionId?: string): boolean => {
      return wsService.current?.sendMessage(_type, _data, _recipients, _sessionId) || false;
    },
    []
  );

  const sendChatMessage = useCallback(
    (
      _message: string,
      _messageType: string = "IC",
      _characterName?: string,
      _recipientId?: string,
      _sessionId?: string
    ): boolean => {
      return (
        wsService.current?.sendChatMessage(
          _message,
          _messageType,
          _characterName,
          _recipientId,
          _sessionId
        ) || false
      );
    },
    []
  );

  const sendDiceRoll = useCallback(
    (
      _formula: string,
      _rollName?: string,
      _isPrivate: boolean = false,
      _characterId?: string,
      _sessionId?: string,
      _characterName?: string
    ): boolean => {
      return (
        wsService.current?.sendDiceRoll(
          _formula,
          _rollName,
          _isPrivate,
          _characterId,
          _sessionId,
          _characterName
        ) || false
      );
    },
    []
  );

  const shareAsset = useCallback(
    (
      _assetId: string,
      _assetName: string,
      _assetType: string,
      _permissions: string[] = [],
      _sessionId?: string
    ): boolean => {
      return (
        wsService.current?.shareAsset(_assetId, _assetName, _assetType, _permissions, _sessionId) ||
        false
      );
    },
    []
  );

  const sendPing = useCallback((): boolean => {
    return wsService.current?.sendPing() || false;
  }, []);

  // Session management methods
  const joinSession = useCallback(
    (_sessionId: string, _characterId?: string, _characterName?: string): boolean => {
      return wsService.current?.joinSession(_sessionId, _characterId, _characterName) || false;
    },
    []
  );

  const leaveSession = useCallback((_sessionId: string): boolean => {
    return wsService.current?.leaveSession(_sessionId) || false;
  }, []);

  // Utility methods
  const clearMessages = useCallback((): void => {
    setMessages([]);
    setLastMessage(null);
  }, []);

  /**
   * Prepend historical messages (e.g. loaded from the REST API) to the message store.
   * Deduplicates by message id to avoid showing messages twice after a reconnect.
   */
  const prependMessages = useCallback((historical: WebSocketMessage[]): void => {
    if (historical.length === 0) return;
    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const newOnes = historical.filter((m) => !existingIds.has(m.id));
      if (newOnes.length === 0) return prev;
      // Historical messages go first (they are older), sorted ascending
      const sorted = [...newOnes].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      return [...sorted, ...prev];
    });
  }, []);

  const updateSessionId = useCallback((_sessionId: string): void => {
    wsService.current?.updateSessionId(_sessionId);
  }, []);

  // Initiative and combat management methods
  const sendInitiativeUpdate = useCallback(
    (
      _sessionId: string,
      _entries: InitiativeEntry[],
      _currentTurn: number,
      _round: number,
      _isTracking: boolean
    ): boolean => {
      return (
        wsService.current?.sendInitiativeUpdate(
          _sessionId,
          _entries,
          _currentTurn,
          _round,
          _isTracking
        ) || false
      );
    },
    []
  );

  const addToInitiative = useCallback((_sessionId: string, _entry: InitiativeEntry): boolean => {
    return wsService.current?.addToInitiative(_sessionId, _entry) || false;
  }, []);

  const removeFromInitiative = useCallback((_sessionId: string, _entryId: string): boolean => {
    return wsService.current?.removeFromInitiative(_sessionId, _entryId) || false;
  }, []);

  const changeTurn = useCallback((_sessionId: string, _direction: "next" | "prev"): boolean => {
    return wsService.current?.changeTurn(_sessionId, _direction) || false;
  }, []);

  const updateCombatState = useCallback((_sessionId: string, _state: string): boolean => {
    return wsService.current?.updateCombatState(_sessionId, _state) || false;
  }, []);

  const updateHealth = useCallback(
    (
      _sessionId: string,
      _entryId: string,
      _hp: number,
      _maxHp: number,
      _change: number,
      _changeType: "damage" | "healing"
    ): boolean => {
      return (
        wsService.current?.updateHealth(_sessionId, _entryId, _hp, _maxHp, _change, _changeType) ||
        false
      );
    },
    []
  );

  const updateStatusEffects = useCallback(
    (
      _sessionId: string,
      _entryId: string,
      _conditions: string[],
      _added?: string[],
      _removed?: string[]
    ): boolean => {
      return (
        wsService.current?.updateStatusEffects(
          _sessionId,
          _entryId,
          _conditions,
          _added,
          _removed
        ) || false
      );
    },
    []
  );

  const sendSessionControl = useCallback((_sessionId: string, _action: string): boolean => {
    return wsService.current?.sendSessionControl(_sessionId, _action) || false;
  }, []);

  return {
    state,
    isConnected: state === WebSocketState.CONNECTED,
    messages,
    lastMessage,
    websocketService: wsService.current,
    connect,
    disconnect,
    sendMessage,
    sendChatMessage,
    sendDiceRoll,
    shareAsset,
    sendPing,
    joinSession,
    leaveSession,
    clearMessages,
    prependMessages,
    updateSessionId,
    connectionInfo,
    sendInitiativeUpdate,
    addToInitiative,
    removeFromInitiative,
    changeTurn,
    updateCombatState,
    updateHealth,
    updateStatusEffects,
    sendSessionControl,
  };
};

// Hook for session-specific WebSocket connection
export const useSessionWebSocket = (
  baseUrl: string,
  sessionId: string,
  userId: string,
  username: string,
  token: string,
  options: UseWebSocketOptions = {}
) => {
  const config: WebSocketConfig = {
    url: baseUrl,
    sessionId,
    userId,
    username,
    token,
  };

  return useWebSocket(config, options);
};

// Hook for filtering messages by type
export const useWebSocketMessages = (
  messages: WebSocketMessage[],
  messageTypes?: MessageType[]
) => {
  return messages.filter(
    (message) => !messageTypes || messageTypes.length === 0 || messageTypes.includes(message.type)
  );
};
